import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Server, Boxes, ArrowRight } from 'lucide-react';
import { Container, PageHeader, Section, Breadcrumbs, BreadcrumbItem } from '@vnetwork/ui/layout';
import { StatusPill, type StatusTone } from '@vnetwork/ui/data';
import { Button, Skeleton } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { useMyServices, serviceLabel, serviceProviderScope } from '@/api/services';
import { OPENNEBULA_SCOPE } from '@/api/opennebula';
import { VMWARE_SCOPE } from '@/api/vmware/_shared';
import { ComputeActivateLanding } from './ComputeActivateLanding';
// Reuse the Services dashboard's provisioned-card grammar so this picker matches
// the compute overview's recent-instance cards (one clickable card idiom).
import cardStyles from '../Services.module.css';

const BASE = '/services/compute';

function statusTone(status: unknown): StatusTone {
  const s = String(status ?? '').toLowerCase();
  if (s === 'active' || s === 'running' || s === 'enabled') return 'success';
  if (s === 'pending' || s === 'provisioning') return 'warning';
  if (/error|fail/.test(s)) return 'danger';
  return 'muted';
}

/**
 * Lists the customer's provisioned OpenNebula (Techev Cloud) services. With a
 * single service it jumps straight into the console — mirrors FastChannel.
 */
export function ComputeServicePicker() {
  const navigate = useNavigate();
  const { data: all = [], isLoading, isError, refetch } = useMyServices();
  // Include both OpenNebula (Techev Cloud) and VMware (Enterprise Compute) services —
  // both open at /services/compute/:serviceId and are dispatched by scope in ComputePage.
  const COMPUTE_SCOPES = new Set([OPENNEBULA_SCOPE, VMWARE_SCOPE]);
  const services = all.filter((s) => COMPUTE_SCOPES.has(serviceProviderScope(s)));

  useEffect(() => {
    if (!isLoading && !isError && services.length === 1) {
      navigate(`${BASE}/${services[0].id}`, { replace: true });
    }
  }, [isLoading, isError, services, navigate]);

  // No services yet — show the activation hero instead of a plain empty-state.
  if (!isLoading && !isError && services.length === 0) {
    return <ComputeActivateLanding />;
  }

  return (
    <Container size="xl">
      <PageHeader
        icon={<Boxes size={22} />}
        breadcrumbs={
          <Breadcrumbs>
            <BreadcrumbItem href="/services" onClick={(e) => { e.preventDefault(); navigate('/services'); }}>Services</BreadcrumbItem>
            <BreadcrumbItem current>Cloud Compute</BreadcrumbItem>
          </Breadcrumbs>
        }
        title="Cloud Compute"
        description="Virtual machines with networking, load balancers and backups."
      />

      <Section title="Your compute services" description="Pick a service to manage its instances.">
        {isError ? (
          <Alert
            tone="danger"
            title="Couldn't load services"
            actions={
              <Button size="sm" variant="secondary" onClick={() => refetch()}>
                Retry
              </Button>
            }
          >
            The service catalog is temporarily unavailable. Please try again.
          </Alert>
        ) : isLoading ? (
          <div className={cardStyles.grid}>
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} style={{ height: 116 }} />
            ))}
          </div>
        ) : (
          <div className={cardStyles.grid}>
            {services.map((svc) => (
              <div
                key={svc.id}
                className={`${cardStyles.card} ${cardStyles.cardClickable}`}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`${BASE}/${svc.id}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate(`${BASE}/${svc.id}`);
                  }
                }}
              >
                <div className={cardStyles.cardHead}>
                  <span className={`${cardStyles.cardIcon} ${cardStyles.cardIconActive}`}>
                    <Server size={19} />
                  </span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div className={cardStyles.cardTitle}>{serviceLabel(svc)}</div>
                    <div className={cardStyles.cardSub}>{svc.provider?.display_name ?? 'Cloud Compute'}</div>
                  </div>
                  <StatusPill tone={statusTone(svc.status)}>{svc.status || 'Unknown'}</StatusPill>
                </div>
                <div className={cardStyles.cardFoot}>
                  <span className={cardStyles.mutedHint}>Manage instances</span>
                  <span className={cardStyles.openHint}>
                    Open <ArrowRight size={14} />
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>
    </Container>
  );
}
